import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

interface Product {
  id: string;
  model_name: string;
  category: string;
  metal_type: string;
  purity_options: any;
  gross_weight: number;
  stone_details: any;
  making_charges: number;
  value_addition: number;
  vendor_id: string;
  description: string;
  status: string;
  images: string[];
  created_at: string;
  updated_at: string;
  delivery_days: number;
  customization_possible: boolean;
}

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate price from making charges and value addition
  const calculatePrice = (product: Product) => {
    const makingCharges = Number(product.making_charges) || 0;
    const valueAddition = Number(product.value_addition) || 0;
    const grossWeight = Number(product.gross_weight) || 0;
    
    // Base calculation: making charges + value addition + estimated gold value
    const estimatedGoldValue = grossWeight * 5000; // Rough estimate at ₹5000 per gram
    return makingCharges + valueAddition + estimatedGoldValue;
  };

  // Get purity string from purity_options
  const getPurity = (product: Product) => {
    if (product.purity_options && Array.isArray(product.purity_options)) {
      return product.purity_options[0] || '';
    }
    return '';
  };

  // Get stone weight from stone_details
  const getStoneWeight = (product: Product) => {
    if (product.stone_details && typeof product.stone_details === 'object') {
      return product.stone_details.total_weight || 0;
    }
    return 0;
  };

  // Calculate net weight
  const getNetWeight = (product: Product) => {
    const grossWeight = Number(product.gross_weight) || 0;
    const stoneWeight = getStoneWeight(product);
    return grossWeight - stoneWeight;
  };

  // Get stone details string
  const getStoneDetailsString = (product: Product) => {
    if (product.stone_details && typeof product.stone_details === 'object') {
      const details = product.stone_details;
      if (details.stones && Array.isArray(details.stones)) {
        return details.stones.map((stone: any) => 
          `${stone.type || 'Stone'} ${stone.weight || 0}ct`
        ).join(', ');
      }
    }
    return 'No stones';
  };

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      setIsLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        
        if (data) {
          setProduct(data);
        } else {
          setError('Product not found');
        }
      } catch (err: any) {
        console.error('Error fetching product:', err);
        setError(err.message || 'Failed to load product');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    console.log('Adding to cart:', { productId: product.id, quantity });
    // Show success message
    alert(`Added ${quantity} ${product.model_name} to cart!`);
  };

  const handleBookOrder = () => {
    if (!product) return;
    navigate('/sales/orders', { state: { selectedProduct: product } });
  };

  const handleAddToWishlist = () => {
    if (!product) return;
    console.log('Adding to wishlist:', product.id);
    alert(`Added ${product.model_name} to wishlist!`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-6">{error || "The product you're looking for doesn't exist."}</p>
          <button
            onClick={() => navigate('/products')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            Back to Catalog
          </button>
        </div>
      </div>
    );
  }

  const productPrice = calculatePrice(product);
  const productPurity = getPurity(product);
  const netWeight = getNetWeight(product);
  const stoneWeight = getStoneWeight(product);
  const stoneDetails = getStoneDetailsString(product);

  // Fallback image if no images
  const productImages = product.images && product.images.length > 0 
    ? product.images 
    : ['/placeholder-jewelry.jpg'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <button
              onClick={() => navigate('/products')}
              className="text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              Catalog
            </button>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-600">{product.category}</span>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{product.model_name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="aspect-square bg-white rounded-lg shadow-lg overflow-hidden">
              <img
                src={productImages[selectedImage]}
                alt={product.model_name}
                className="w-full h-full object-cover object-center"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-jewelry.jpg';
                }}
              />
            </div>
            {productImages.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {productImages.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`aspect-square bg-white rounded-lg overflow-hidden border-2 cursor-pointer ${
                      selectedImage === index ? 'border-blue-600' : 'border-gray-200'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.model_name} ${index + 1}`}
                      className="w-full h-full object-cover object-center"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder-jewelry.jpg';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Information */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.model_name}</h1>
              <p className="text-lg text-gray-600">{product.description || 'Premium jewelry piece'}</p>
              <div className="flex items-center space-x-4 mt-2">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm font-medium">
                  {product.category}
                </span>
                {product.customization_possible && (
                  <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                    Customizable
                  </span>
                )}
              </div>
            </div>

            {/* Price */}
            <div className="border-t border-b border-gray-200 py-6">
              <div className="flex items-center space-x-4">
                <span className="text-3xl font-bold text-gray-900">
                  ₹{productPrice.toLocaleString('en-IN')}
                </span>
                <span className="text-lg text-gray-500 line-through">
                  ₹{Math.round(productPrice * 1.2).toLocaleString('en-IN')}
                </span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium">
                  Save ₹{Math.round(productPrice * 0.2).toLocaleString('en-IN')}
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Delivery in {product.delivery_days || 7} days
              </p>
            </div>

            {/* Product Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Product Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Category</span>
                  <p className="font-medium text-gray-900">{product.category}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Metal Type</span>
                  <p className="font-medium text-gray-900">{product.metal_type} {productPurity}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Gross Weight</span>
                  <p className="font-medium text-gray-900">{Number(product.gross_weight).toFixed(2)}g</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Net Weight</span>
                  <p className="font-medium text-gray-900">{netWeight.toFixed(2)}g</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Stone Weight</span>
                  <p className="font-medium text-gray-900">{stoneWeight.toFixed(2)}g</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Stone Details</span>
                  <p className="font-medium text-gray-900">{stoneDetails}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Making Charges</span>
                  <p className="font-medium text-gray-900">₹{Number(product.making_charges).toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <span className="text-sm text-gray-600">Value Addition</span>
                  <p className="font-medium text-gray-900">₹{Number(product.value_addition).toLocaleString('en-IN')}</p>
                </div>
              </div>
            </div>

            {/* Quantity Selector */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Quantity</h3>
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-subtract-line"></i>
                </button>
                <span className="text-xl font-medium w-12 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-50 cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <button
                onClick={handleAddToCart}
                className="w-full bg-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-cart-line mr-2"></i>
                Add to Cart
              </button>
              <button
                onClick={handleBookOrder}
                className="w-full bg-green-600 text-white py-4 rounded-lg font-semibold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bookmark-line mr-2"></i>
                Book Order
              </button>
              <button
                onClick={handleAddToWishlist}
                className="w-full border border-gray-300 text-gray-700 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line mr-2"></i>
                Add to Wishlist
              </button>
            </div>

            {/* Additional Information */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Why Choose This Product?</h4>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Certified authentic jewelry
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  30-day return policy
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Free shipping on orders above ₹25,000
                </li>
                <li className="flex items-center">
                  <i className="ri-check-line mr-2"></i>
                  Lifetime warranty on gold purity
                </li>
                {product.customization_possible && (
                  <li className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    Customization available
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;